<template>
  <div class="bg-surface-raised flex flex-col gap-2 rounded-lg p-4">
    <div class="flex shrink-0 flex-row items-start">
      <div class="flex flex-1 flex-col">
        <div class="capitalize-first font-bold">
          {{
            $h.formatDateAndDiff(
              activity.created_at,
              'LLLL',
              $i18n.locale.substring(0, 2)
            )
          }}
        </div>
        <div class="text-xs leading-none font-semibold">
          # {{ $t(`enums.activityLog.type.${activity.description}`) }}
        </div>
        <RouterLink
          v-if="subjectLabel && subjectRoute"
          :to="subjectRoute"
          class="mt-0.5 text-sm text-blue-500 hover:underline dark:text-blue-400"
        >
          {{ subjectLabel }}
        </RouterLink>
        <div
          v-else-if="subjectLabel"
          class="mt-0.5 text-sm text-gray-500 dark:text-gray-400"
        >
          {{ subjectLabel }}
        </div>
      </div>
      <div class="flex shrink-0 flex-col">
        <button
          v-if="isPaymentSyncCauser"
          type="button"
          class="flew-row flex flex-1 items-center justify-end gap-2 hover:underline"
          :title="$t('invoicing.payment_sync_run_detail')"
          @click="openSyncRun"
        >
          <span class="font-semibold">
            {{ $t('invoicing.payment_sync_causer') }}
          </span>
          <img :src="chiftLogo" class="h-4" alt="" />
        </button>
        <div v-else class="flew-row flex flex-1 items-center justify-end gap-2">
          <span class="font-semibold">{{ user?.full_name }}</span>
          <UserAvatarPopover :user-id="activity.causer_id" size="extra-small" />
        </div>
      </div>
    </div>
    <div
      v-if="isModelActivity && propertiesAsArray.length"
      class="overflow-hidden rounded border"
    >
      <table class="w-full">
        <tr
          v-for="property in propertiesAsArray"
          :key="property.id"
          class="odd:bg-gray-100 dark:odd:bg-gray-800"
        >
          <td class="w-1/3 py-1.5 pl-3 font-bold">
            {{ $t(`attributes.${property.attribute}`) }}
          </td>
          <td class="w-1/3 py-1.5">{{ property.old }}</td>
          <td class="w-1/3 py-1.5">{{ property.new }}</td>
        </tr>
      </table>
    </div>
    <div v-else-if="isContactActivity" class="empty:hidden">
      <textarea
        v-if="activity.properties?.notes?.length"
        class="bg-surface-overlay min-h-20 w-full resize-none rounded-md p-2"
        :value="activity.properties.notes"
        readonly
      ></textarea>
    </div>
    <div
      v-if="activity.tasks?.length"
      class="mt-1 grid grid-cols-2 gap-2 md:grid-cols-3"
    >
      <div
        v-for="task in activity.tasks"
        :key="task.id"
        class="group flex rounded-md shadow-sm dark:border dark:border-white dark:bg-slate-700"
      >
        <div
          class="flex flex-1 items-center justify-between gap-2 truncate rounded-md border border-gray-200 bg-white px-2 py-1 dark:bg-slate-700"
        >
          <div class="flex-1 truncate text-sm">
            <h4 class="truncate font-medium text-gray-900 dark:text-white">
              {{ task.name?.[$i18n.locale] }}
            </h4>
            <p v-if="task.due_date_time" class="text-xs text-gray-500 dark:text-gray-400">
              {{ $h.formatDate(task.due_date_time, task.due_date_undefined_time ? 'll' : 'lll') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <ChiftPaymentSyncRunModal
      v-if="openedRunId"
      :run-id="openedRunId"
      @close="openedRunId = null"
    />
  </div>
</template>

<script>
import UserAvatarPopover from '@c/Avatar/Popover/UserAvatarPopover.vue'
import ChiftPaymentSyncRunModal from '@c/Partner/Chift/ChiftPaymentSyncRunModal.vue'
import PartnersLogo from '@/assets/enums/config/PartnersLogo'
import { mapGetters } from 'vuex'
import { CONTACT_EVENTS } from '@u/constants'

export default {
  name: 'ActivityBlock',
  components: {
    UserAvatarPopover,
    ChiftPaymentSyncRunModal
  },
  props: {
    activity: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      openedRunId: null
    }
  },
  computed: {
    ...mapGetters({
      users: 'metadata/getAllUserMetadata',
      initialMetadata: 'metadata/getInitialMetadata',
      dossier: 'dossier/getDossier'
    }),
    user() {
      return this.users(this.activity.causer_id)
    },
    /**
     * The activity causer is a model, not necessarily a User. A scheduled sync has no
     * authenticated user, so it causes its writes as the run itself — which answers
     * "by whom" and "by which run" in one go.
     */
    isPaymentSyncCauser() {
      return this.activity.causer_type === 'App\\Models\\PaymentSyncRun'
    },
    chiftLogo() {
      return PartnersLogo.chift
    },
    subjectLabel() {
      const subject = this.activity.subject
      if (!subject) return null
      if (this.activity.subject_type === 'App\\Models\\Company') {
        return `${this.$t('attributes.company')} : ${subject.name}`
      } else if (this.activity.subject_type === 'App\\Models\\FuneralHome') {
        return `${this.$t('attributes.funeral_home')} : ${subject.name}`
      } else if (this.activity.subject_type === 'App\\Models\\Site') {
        return `${this.$t('attributes.site')} : ${subject.name}`
      } else if (this.activity.subject_type === 'App\\Models\\Dossier') {
        return [
          this.$t('attributes.dossier'),
          ':',
          subject.reference,
          subject.concernedPerson?.full_name
        ]
          .filter(Boolean)
          .join(' ')
      } else if (this.activity.subject_type === 'App\\Models\\Person') {
        return `${this.$t('attributes.person')} : ${subject.full_name}`
      } else if (this.activity.subject_type === 'App\\Models\\Event') {
        return `${this.$t('attributes.event')} : ${subject.title}`
      } else if (this.activity.subject_type === 'App\\Models\\TaskDossier') {
        return `${this.$t('attributes.task')} : ${subject.name[this.$i18n.locale]}`
      }
      const subjectType = this.activity.subject_type?.split('\\').pop()
      const name =
        typeof subject.name === 'object'
          ? (subject.name[this.$i18n.locale] ??
            subject.name[Object.keys(subject.name)[0]] ??
            null)
          : (subject.name ?? null)

      if (subjectType === 'WikiPlace' && subject.type) {
        const typeLabel = this.initialMetadata?.enums?.place_types?.find(
          (p) => p.id === subject.type
        )?.name
        return [typeLabel, name].filter(Boolean).join(' — ')
      }

      return name
    },
    subjectRoute() {
      if (
        this.activity.subject_type === 'App\\Models\\Dossier' &&
        this.activity.subject_id
      ) {
        return {
          name: 'dossier_form_edit_route',
          params: { id: this.activity.subject_id }
        }
      }
      return null
    },
    isContactActivity() {
      return CONTACT_EVENTS.includes(this.activity.description)
    },
    isModelActivity() {
      return !!this.activity.properties.attributes
    },
    propertiesAsArray: function () {
      if (!this.activity.properties.attributes) {
        return []
      }

      const normalize = (value) => {
        if (value === null || value === undefined) return null
        if (value === '' || value === '---') return null
        if (value === 0 || value === 0.0 || value === '0' || value === '0.00')
          return null
        return value
      }

      return Object.keys(this.activity.properties.attributes)
        .map((key) => ({
          attribute: key,
          old:
            this.formatValue(normalize(this.activity.properties.old?.[key])) ??
            '---',
          new:
            this.formatValue(
              normalize(this.activity.properties.attributes[key])
            ) ?? '---'
        }))
        .filter((line) => {
          const oldVal = line.old
          const newVal = line.new

          const isDifferent = JSON.stringify(oldVal) !== JSON.stringify(newVal)

          return isDifferent
        })
    }
  },
  methods: {
    openSyncRun() {
      this.openedRunId = this.activity.causer_id
    },
    formatValue(value) {
      if (value === null || value === undefined) return null
      if (Array.isArray(value)) {
        const labels = value
          .map((item) => this.formatItem(item))
          .filter(Boolean)
        return labels.length ? labels.join(', ') : null
      }
      if (typeof value === 'object') {
        return this.formatItem(value)
      }
      return value
    },
    formatItem(item) {
      if (typeof item !== 'object' || item === null) {
        const dossierItem = this.dossier?.dossier_items?.find(
          (di) => String(di.id) === String(item) || String(di.item_id) === String(item)
        )
        if (dossierItem?.item?.name) {
          const name = dossierItem.item.name
          return typeof name === 'object'
            ? (name[this.$i18n.locale] ?? name[Object.keys(name)[0]] ?? String(item))
            : String(name)
        }
        return String(item)
      }
      // Ressource humaine (user)
      if (item.full_name) return item.full_name
      // Ressource matérielle avec nom traduit
      if (item.name && typeof item.name === 'object') {
        return (
          item.name[this.$i18n.locale] ??
          item.name[Object.keys(item.name)[0]] ??
          item.initials ??
          null
        )
      }
      if (typeof item.name === 'string') return item.name
      return item.initials ?? null
    }
  }
}
</script>
